import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  createOrganisation,
  deleteOrganisation,
  fetchOrganisations,
} from "../../../api/organisations";
import { ApiError } from "../../../api/client";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../contexts/AuthProvider";
import { FieldLabel, TextInput } from "../_components/AdminShell";
import { AdminShell } from "../_components/AdminShell";
import type { OrganisationDto } from "../../../types/organisation";

export default function OrganisationsListPage() {
  const { canManageOrganisations } = useAuth();
  const [items, setItems] = useState<OrganisationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchOrganisations());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось загрузить организации",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Организации";
    void reload();
  }, []);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    setCreating(true);
    setActionError(null);
    try {
      await createOrganisation({
        name: name.trim(),
        slug: slug.trim(),
        isActive: true,
      });
      setName("");
      setSlug("");
      await reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.message
          : "Не удалось создать организацию",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number, orgName: string) => {
    if (!window.confirm(`Удалить организацию «${orgName}»?`)) return;

    setActionError(null);
    try {
      await deleteOrganisation(id);
      await reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.message
          : "Не удалось удалить организацию",
      );
    }
  };

  return (
    <AdminShell
      compact
      title="Организации"
      backLink={{ to: "/", label: "К формам" }}
    >
      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {actionError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-sm text-red-700">{actionError}</p>
        </div>
      ) : null}

      {canManageOrganisations ? (
        <form
          onSubmit={(event) => void handleCreate(event)}
          className="mb-4 rounded-xl border border-slate-200/80 bg-white p-4 shadow-card-lg"
        >
          <h2 className="font-onest text-sm font-semibold text-ink">
            Новая организация
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="org-name" size="compact">
                Название
              </FieldLabel>
              <TextInput
                id="org-name"
                size="compact"
                value={name}
                onChange={setName}
              />
            </div>
            <div>
              <FieldLabel htmlFor="org-slug" size="compact">
                Slug
              </FieldLabel>
              <TextInput
                id="org-slug"
                size="compact"
                value={slug}
                onChange={setSlug}
                placeholder="moscow"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="mt-3 px-4 py-2 text-sm"
            disabled={creating}
          >
            {creating ? "Создание…" : "Создать"}
          </Button>
        </form>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-card-lg">
        {loading ? (
          <div className="px-4 py-8 text-center font-onest text-sm text-slate-500">
            Загрузка…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center font-onest text-sm text-slate-500">
            Организаций пока нет
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((org) => (
              <li
                key={org.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div>
                  <Link
                    to={`/admin/organisations/${org.id}`}
                    className="font-onest text-sm font-semibold text-brand-700 hover:underline"
                  >
                    {org.name}
                  </Link>
                  <p className="font-onest text-xs text-slate-500">
                    {org.slug}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/organisations/${org.id}`}
                    className="rounded-md px-2 py-1 font-onest text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Участники
                  </Link>
                  {canManageOrganisations ? (
                    <button
                      type="button"
                      onClick={() => void handleDelete(org.id, org.name)}
                      className="rounded-md px-2 py-1 font-onest text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Удалить
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminShell>
  );
}
